import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function PMS() {
  const { pms, loading, isAdmin } = useWebData(); 

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 0. DATE HELPER (Add 3 Months)
  // ==========================================
  const addThreeMonths = (dateStr) => {
      const d = new Date(dateStr);
      d.setMonth(d.getMonth() + 3);
      return d.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!pms) return;

    // A. Prepare Data (Smart Date Logic)
    let result = pms.map(d => ({
        ...d,
        // Filter Date: Agar 'Done' hai to Service Date, nahi to Next Due Date
        filterDate: d.status === 'Done' ? (d.date || d.createdAt) : (d.nextServiceDate || d.scheduledDate)
    }));

    // B. Search Filter
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = formatDate(i.filterDate).toLowerCase();
            const row = `${dateStr} ${i.hospital} ${i.city} ${i.machine} ${i.model} ${i.serialNo} ${i.status} ${i.userName} ${i.remarks}`.toLowerCase();
            return row.includes(lower);
        });
    }

    // C. Date Filter (Universal)
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.filterDate || ""; 
            if (!rawDate) return false;

            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }

            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    // D. Sort (Newest First)
    result.sort((a, b) => new Date(b.filterDate || 0) - new Date(a.filterDate || 0));
    setFiltered(result);
  }, [pms, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Service_Date: formatDate(i.date), 
        Next_Due_Date: formatDate(i.nextServiceDate),
        Hospital: i.hospital,
        City: i.city || '-',
        Machine: i.machine,
        Model: i.model,
        Serial_No: i.serialNo,
        PMS_Count: `PMS ${i.currentPmsNumber || 1} of ${i.totalPms || 4}`,
        Remarks: i.remarks || "-", 
        Status: i.status,
        Engineer: i.userName
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PMS_Report");
    XLSX.writeFile(wb, `PMS_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  
  // 🔥 UPDATE 1: MARK DONE (Auto +3 Months)
  const markDone = async (id) => {
      if(window.confirm("Mark PMS as Completed?")) {
          const today = new Date().toISOString().split('T')[0];
          const nextDate = addThreeMonths(today); // 🔥 3 Months added automatically

          await updateDoc(doc(db, "pms_reports", id), { 
              status: "Done",
              date: today,            // Service Date = Today
              nextServiceDate: nextDate // Next Due = Today + 3 Months
          });
          alert(`PMS Done! Next Due Date set to: ${formatDate(nextDate)}`);
      }
  };

  // 🔥 UPDATE 2: EDIT HANDLER (Auto Update on Date Change)
  const handleEditDateChange = (newDate) => {
      setEditData({
          ...editData,
          date: newDate,
          nextServiceDate: addThreeMonths(newDate) // 🔥 Auto calculate next date
      });
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "pms_reports", editData.id), {
              date: editData.date, 
              nextServiceDate: editData.nextServiceDate, 
              hospital: editData.hospital,
              machine: editData.machine,
              model: editData.model,
              serialNo: editData.serialNo,
              remarks: editData.remarks,
              status: editData.status,
              currentPmsNumber: editData.currentPmsNumber,
              totalPms: editData.totalPms
          });
          alert("PMS Record Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this record?")) await deleteDoc(doc(db, "pms_reports", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#8e44ad", display:"flex", alignItems:"center"}}>
                🛠️ PMS Manager <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Machine, Date..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#8e44ad", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Service Date</th>
                    <th style={thStyle}>Next Due</th>
                    <th style={thStyle}>Hospital & City</th>
                    <th style={thStyle}>Machine Info</th>
                    <th style={thStyle}>PMS Cycle</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Action</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No records found.</td></tr>
              ) : filtered.map(i => {
                const today = new Date().toISOString().split('T')[0];
                const isOverdue = i.status !== 'Done' && (i.nextServiceDate < today);
                return (
                    <tr key={i.id} style={{borderBottom:"1px solid #eee", height:"45px", backgroundColor: isOverdue ? "#fff0f0" : "white"}}>
                        
                        {/* Actual Service Date */}
                        <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>
                            {formatDate(i.date)}
                        </td>

                        {/* Next Due Date */}
                        <td style={{...tdStyle, color: isOverdue?'red':'#7f8c8d'}}>
                            {formatDate(i.nextServiceDate)}
                            {isOverdue && <div style={{fontSize:"9px", color:"red"}}>⚠️ Overdue</div>}
                        </td>

                        <td style={{...tdStyle, textAlign:"left"}}>
                            <div style={{fontWeight:"bold"}}>{i.hospital}</div>
                            <div style={{fontSize:"10px", color:"gray"}}>{i.city || '-'}</div>
                        </td>
                        <td style={tdStyle}>
                            <div style={{fontWeight:"bold"}}>{i.machine}</div>
                            <div style={{fontSize:"10px", color:"#555"}}>{i.model}</div>
                        </td>
                        <td style={{...tdStyle, color:"blue", fontWeight:"bold"}}>
                            PMS {i.currentPmsNumber || 1}/{i.totalPms || 4}
                        </td>
                        <td style={{...tdStyle, fontWeight:"bold", color: i.status==='Done'?'green':'orange'}}>{i.status}</td>
                        <td style={tdStyle}>
                            <div style={{display:"flex", gap:"3px"}}>
                                {isAdmin && i.status !== 'Done' && (
                                    <button onClick={()=>markDone(i.id)} style={btnCheck} title="Mark Done (Auto +3M)">✔</button>
                                )}
                                <button onClick={() => setViewData(i)} style={btnIcon} title="View">👁️</button>
                                {isAdmin && (
                                    <>
                                        <button onClick={() => setEditData(i)} style={btnIconEdit} title="Edit">✏️</button>
                                        <button onClick={() => handleDelete(i.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                    </>
                                )}
                            </div>
                        </td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#8e44ad", marginTop:0, borderBottom:"1px solid #eee", paddingBottom:"10px"}}>🛠️ PMS Details</h3>
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <div style={{display:"flex", justifyContent:"space-between", marginBottom:"10px", background:"#f0f4ff", padding:"10px", borderRadius:"5px"}}>
                    <div>
                        <p style={{margin:0, fontSize:"11px", color:"#555"}}>Service Date</p>
                        <p style={{margin:0, fontWeight:"bold", fontSize:"14px"}}>{formatDate(viewData.date)}</p>
                    </div>
                    <div style={{textAlign:"right"}}>
                        <p style={{margin:0, fontSize:"11px", color:"#555"}}>Next Due</p>
                        <p style={{margin:0, fontWeight:"bold", fontSize:"14px", color:"red"}}>{formatDate(viewData.nextServiceDate)}</p>
                    </div>
                </div>

                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px"}}>
                    <div>
                        <p><strong>🏥 Hospital:</strong> {viewData.hospital}</p>
                        <p><strong>📍 City:</strong> {viewData.city}</p>
                        <p><strong>⚙️ Machine:</strong> {viewData.machine}</p>
                    </div>
                    <div>
                        <p><strong>🏷️ Model:</strong> {viewData.model}</p>
                        <p><strong>🔢 Serial:</strong> {viewData.serialNo}</p>
                        <p><strong>👷 Engineer:</strong> {viewData.userName}</p>
                    </div>
                </div>
                
                <hr style={{border:"0.5px solid #eee"}}/>
                <div style={{background:"#fff3e0", padding:"10px", borderRadius:"5px", borderLeft:"4px solid #e67e22", marginTop:"10px"}}>
                    <p style={{margin:0, fontWeight:"bold", color:"#e67e22"}}>📝 Remarks / Observations:</p>
                    <p style={{marginTop:"5px"}}>{viewData.remarks || "No remarks added."}</p>
                </div>

                <p style={{marginTop:"10px", textAlign:"right"}}><strong>🔥 Status:</strong> {viewData.status}</p>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Record</h3>
            
            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Service Date (Done)</label>
                    {/* 🔥 Auto Calc Triggered here */}
                    <input type="date" value={editData.date ? editData.date.split('T')[0] : ''} onChange={(e)=>handleEditDateChange(e.target.value)} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={{...labelStyle, color:"red"}}>Next Due Date</label>
                    <input type="date" value={editData.nextServiceDate} onChange={(e)=>setEditData({...editData, nextServiceDate:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <label style={labelStyle}>Hospital</label>
            <input type="text" value={editData.hospital} onChange={(e)=>setEditData({...editData, hospital:e.target.value})} style={modalInput}/>

            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Current PMS No.</label>
                    <input type="number" value={editData.currentPmsNumber || 1} onChange={(e)=>setEditData({...editData, currentPmsNumber:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Total PMS</label>
                    <input type="number" value={editData.totalPms || 4} onChange={(e)=>setEditData({...editData, totalPms:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <label style={labelStyle}>Remarks / Notes</label>
            <textarea value={editData.remarks} onChange={(e)=>setEditData({...editData, remarks:e.target.value})} style={{...modalInput, height:"60px"}} placeholder="Enter observation..."></textarea>

            <label style={labelStyle}>Status</label>
            <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                <option value="Pending">Pending ⏳</option>
                <option value="Done">Done ✅</option>
                <option value="Missed">Missed ❌</option>
            </select>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnCheck={backgroundColor:"#27ae60", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"400px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#8e44ad", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default PMS;